import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ImageOff, Plus, Users } from 'lucide-react';
import { StatsGrid, StatsGridSkeleton } from '../components/dashboard/StatsGrid';
import { ExportMenu } from '../components/export/ExportMenu';
import { StudentsEmptyState } from '../components/students/StudentsEmptyState';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useStudents } from '../contexts/StudentsContext';
import { initialsOf, orDash, relativeTime } from '../utils/format';
import { classDistribution, computeStats } from '../utils/stats';
export function Dashboard() {
  const {
    user
  } = useAuth();
  const {
    students,
    loading,
    openCreateForm,
    openDetail
  } = useStudents();
  const stats = useMemo(() => computeStats(students), [students]);
  const distribution = useMemo(() => classDistribution(students).slice(0, 6), [students]);
  const recent = useMemo(() => students.slice(0, 5), [students]);
  const maxCount = distribution[0]?.count ?? 1;
  const firstName = (user?.displayName || user?.email || 'there').split(/[\s@]/)[0];
  return <div className="mx-auto w-full max-w-7xl space-y-6" data-id="element-481" data-mp="2bo46quodoib9">
      <section className="glass flex flex-col gap-5 rounded-3xl p-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-482" data-mp="2bom7o4lzjkpi">
        <div className="min-w-0" data-id="element-483" data-mp="2fn3gqtoyv59k">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground" data-id="element-484" data-mp="1izyivz212d77">
            {new Date().toLocaleDateString(undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          })}
          </p>
          <h2 className="mt-1.5 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl" data-id="element-485" data-mp="1rysmi9ds03u9">
            Welcome back, <span className="gradient-text" data-id="element-486" data-mp="1ws4ynk19qi64">{firstName}</span>
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground" data-id="element-487" data-mp="109h28sugz827">
            {stats.total === 0 ? 'Your Firestore collection is empty — add your first student to begin.' : `You are tracking ${stats.total} student${stats.total === 1 ? '' : 's'} across ${stats.classes} class${stats.classes === 1 ? '' : 'es'}.`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-488" data-mp="2hb9lm22yjn0h">
          <ExportMenu data-id="element-489" data-mp="2cbwwtbsj05v9" />
          <Button onClick={openCreateForm} className="h-9 rounded-xl brand-gradient px-4 text-white shadow-lg shadow-indigo-500/25 hover:opacity-95" data-id="element-490" data-mp="2cbdfnxap9lia">
            <Plus className="mr-1.5 h-4 w-4" data-id="element-491" data-mp="1jnhi6qdb3pkg" />
            Add Student
          </Button>
        </div>
      </section>

      {loading ? <StatsGridSkeleton data-id="element-492" data-mp="1xo21ror1ppf0" /> : <StatsGrid stats={stats} data-id="element-493" data-mp="1xok6zavordz2" />}

      {!loading && stats.total === 0 ? <section className="glass rounded-3xl" data-id="element-494" data-mp="1tkced2m3xdvk">
          <StudentsEmptyState variant="no-records" onAddStudent={openCreateForm} data-id="element-495" data-mp="1tnrxcgiwj729" />
        </section> : <div className="grid gap-4 lg:grid-cols-5" data-id="element-496" data-mp="350kkfe0qz7bz">
          <section className="glass rounded-3xl p-6 lg:col-span-2" aria-label="Students per class" data-id="element-497" data-mp="35013o0mu2ozj">
            <h3 className="text-sm font-semibold tracking-tight text-foreground" data-id="element-498" data-mp="3ciij0nhe4y0f">Students per class</h3>
            <p className="mt-1 text-xs text-muted-foreground" data-id="element-499" data-mp="3hkhlzw97xswu">Top classes by number of records</p>

            <div className="mt-5 space-y-4" data-id="element-500" data-mp="32nqjvf85u33j">
              {loading && Array.from({
            length: 4
          }).map((_, index) => <div key={index} className="space-y-2" data-id="element-501" data-mp="3val5ifgpr8il" data-mpi={index}>
                    <div className="shimmer h-2.5 w-1/3 rounded-full" data-id="element-502" data-mp="3vb34hjurg0u7" />
                    <div className="shimmer h-2 w-full rounded-full" data-id="element-503" data-mp="438ihjt5g0ur" />
                  </div>)}

              {!loading && distribution.map((bucket, index) => <div key={bucket.className} data-id="element-504" data-mp="3qj345qas35x3" data-mpi={index}>
                    <div className="flex items-center justify-between text-sm" data-id="element-505" data-mp="3qhs0dqhc1y5z">
                      <span className="truncate font-medium text-foreground" data-id="element-506" data-mp="i2qd4ft4m8c1">{bucket.className}</span>
                      <span className="font-mono text-xs text-muted-foreground" data-id="element-507" data-mp="mw2o3n4vugmb">{bucket.count}</span>
                    </div>
                    <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[var(--glass-hairline)]" data-id="element-508" data-mp="2qcr4r18dlkzq">
                      <motion.div initial={{
                width: 0
              }} animate={{
                width: `${Math.max(bucket.count / maxCount * 100, 6)}%`
              }} transition={{
                duration: 0.6,
                delay: index * 0.05,
                ease: 'easeOut'
              }} className="h-full rounded-full brand-gradient" data-id="element-509" data-mp="2ldockhdhv996" />
                    </div>
                  </div>)}
            </div>
          </section>

          <section className="glass rounded-3xl p-6 lg:col-span-3" aria-label="Recent additions" data-id="element-510" data-mp="34apj46mp5hf8">
            <div className="flex items-center justify-between" data-id="element-511" data-mp="2zd5tkakjti7z">
              <div data-id="element-512" data-mp="oh25o9lx7zh">
                <h3 className="text-sm font-semibold tracking-tight text-foreground" data-id="element-513" data-mp="p09kb1msdz9">Recent additions</h3>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-514" data-mp="3s0jqi8o8yds3">The latest records written to Firestore</p>
              </div>
              <Link to="/students" className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline" data-id="element-515" data-mp="47pj2sy6iuyh">
                View all
                <ArrowRight className="h-3.5 w-3.5" data-id="element-516" data-mp="d8dxvpveczcf" />
              </Link>
            </div>

            <ul className="mt-4 divide-y divide-[var(--glass-hairline)]" data-id="element-517" data-mp="i2rolez46iar">
              {loading && Array.from({
            length: 4
          }).map((_, index) => <li key={index} className="flex items-center gap-3 py-3" data-id="element-518" data-mp="r4hol972apk5" data-mpi={index}>
                    <div className="shimmer h-10 w-10 rounded-xl" data-id="element-519" data-mp="vw984zrsvv0s" />
                    <div className="flex-1 space-y-2" data-id="element-520" data-mp="10vm3k8wqmaez">
                      <div className="shimmer h-3 w-1/3 rounded-full" data-id="element-521" data-mp="10v412ujhezym" />
                      <div className="shimmer h-2.5 w-1/4 rounded-full" data-id="element-522" data-mp="15vi0jo70p1l1" />
                    </div>
                  </li>)}

              {!loading && recent.map(student => <li key={student.id} data-id="element-523" data-mp="l6pdps2flmhr">
                    <button type="button" onClick={() => openDetail(student)} className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:opacity-80" data-id="element-524" data-mp="l3bdapdbvncy">
                      <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-xl border border-[var(--glass-border)] bg-muted text-[11px] font-semibold text-muted-foreground" data-id="element-525" data-mp="q3z1z4e33s40">
                        {student.photoURL ? <img src={student.photoURL} alt={student.fullNames || 'Student photo'} className="h-full w-full object-cover" data-id="element-526" data-mp="1dubhb0bnsr4m" /> : student.fullNames.trim() ? initialsOf(student.fullNames) : <ImageOff className="h-4 w-4" data-id="element-527" data-mp="3aoy837ldudhw" />}
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-528" data-mp="1v3ns9i5crc3m">
                        <span className="block truncate text-sm font-medium text-foreground" data-id="element-529" data-mp="1v34emdmqj9ve">
                          {orDash(student.fullNames)}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground" data-id="element-530" data-mp="1lcsy7rlv787m">
                          {[student.className.trim(), student.gender].filter(Boolean).join(' · ') || 'No class yet'}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground" data-id="element-531" data-mp="xg5bbk1jf3oj">
                        {relativeTime(student.createdAtMs)}
                      </span>
                    </button>
                  </li>)}

              {!loading && recent.length === 0 && <li className="flex items-center gap-3 py-8 text-sm text-muted-foreground" data-id="element-532" data-mp="2wsznnxcdjhos">
                  <Users className="h-4 w-4" data-id="element-533" data-mp="2wqn97u5trpgh" />
                  No records yet.
                </li>}
            </ul>
          </section>
        </div>}
    </div>;
}